import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { SuperAdminAPI } from "../api";
import { HR_TEAM_LOCATIONS } from "../../config/locationConfig";
import AdminIcon from "./adminIcons";
import {
  ADMIN_BUTTON_SX,
  ADMIN_COLORS,
  ADMIN_ICON_SIZE,
  ADMIN_SWITCH_SX,
  AdminPage,
  AdminSheet,
  AdminSheetHeader,
} from "./adminUi";
import {
  ADMIN_FORM_GRID_SX,
  AdminDialog,
  AdminTextField,
  PhonePrefix,
  submitWithValidation,
} from "./adminForm";
import { FIELD_MAX, cleanText, fieldSchema, sanitize } from "./adminValidation";
import AdminDrawer from "./AdminDrawer";
import AdminListToolbar from "./AdminListToolbar";
import AdminTable from "./AdminTable";
import {
  RECORD_INFO_GRID_SX,
  RecordInfoItem,
  RecordLoginSwitch,
  RecordProfileHeader,
  RecordSectionHeader,
} from "./AdminRecordParts";

var TOAST_OPTS = { hideProgressBar: true, closeOnClick: true };

var ACTION_ICON_BUTTON_SX = {
  p: "6px",
  color: ADMIN_COLORS.label,
  "&:hover": { color: ADMIN_COLORS.brand, bgcolor: ADMIN_COLORS.brandSoft },
};


var EMPTY_HR = {
  name: "",
  email: "",
  phoneNumber: "",
  designation: "",
  workLocation: "",
  hrTeamLocation: "",
};

var HR_SCHEMA = Yup.object({
  name: fieldSchema.name(),
  email: fieldSchema.hrEmail(),
  phoneNumber: fieldSchema.phone(),
  designation: fieldSchema.designation(),
  workLocation: fieldSchema.location("Work location"),
  hrTeamLocation: fieldSchema.select("Please select an HR team location", HR_TEAM_LOCATIONS),
});

function matchLocation(raw) {
  var value = String(raw || "").trim();
  return (
    HR_TEAM_LOCATIONS.find(function (loc) {
      return loc.toLowerCase() === value.toLowerCase();
    }) || value
  );
}

function toRows(items) {
  return (items || []).map(function (item) {
    return {
      id: item._id,
      name: item.name,
      email: item.email,
      phoneNumber: item.phoneNumber,
      designation: item.designation,
      workLocation: item.workLocation,
      hrTeamLocation: item.hrTeamLocation || item.workLocation,
      isActive: item.isActive,
    };
  });
}

function matchesHrSearch(row, search) {
  var needle = String(search || "").trim().toLowerCase();
  if (!needle) return true;
  return ["name", "designation", "email"].some(function (field) {
    return String(row[field] || "").toLowerCase().indexOf(needle) !== -1;
  });
}

function HrDialog(props) {
  var isEdit = props.mode === "edit";
  var formik = useFormik({
    initialValues: props.initialValues || EMPTY_HR,
    enableReinitialize: true,
    validationSchema: HR_SCHEMA,
    onSubmit: function (values, helpers) {
      return Promise.resolve(props.onSubmit(values)).finally(function () {
        helpers.setSubmitting(false);
      });
    },
  });
  var values = formik.values;

  function fieldError(name) {
    return formik.touched[name] && formik.errors[name] ? formik.errors[name] : "";
  }

  function setValue(name, value) {
    formik.setFieldTouched(name, true, false);
    formik.setFieldValue(name, value, true);
  }

  function handleClose() {
    if (formik.isSubmitting) return;
    formik.resetForm();
    props.onClose();
  }

  var hasLegacyLocation = values.hrTeamLocation && HR_TEAM_LOCATIONS.indexOf(values.hrTeamLocation) === -1;

  return (
    <AdminDialog
      open={props.open}
      onClose={handleClose}
      busy={formik.isSubmitting}
      title={isEdit ? "Edit HR" : "Add HR"}
      titleId="hr-dialog-title"
      onSubmit={submitWithValidation(formik)}
      actions={
        <>
          <Button variant="outlined" onClick={handleClose} disabled={formik.isSubmitting} sx={ADMIN_BUTTON_SX.outlined}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting}
            startIcon={formik.isSubmitting ? <CircularProgress size={ADMIN_ICON_SIZE.button} color="inherit" /> : null}
            sx={ADMIN_BUTTON_SX.contained}
          >
            {isEdit ? "Update" : "Create"}
          </Button>
        </>
      }
    >
      <Box sx={ADMIN_FORM_GRID_SX}>
        <AdminTextField
          required
          label="Full Name"
          name="name"
          placeholder="Write Name"
          value={values.name}
          onChange={function (e) {
            setValue("name", sanitize.name(e.target.value));
          }}
          onBlur={formik.handleBlur}
          inputProps={{ maxLength: FIELD_MAX.name }}
          error={fieldError("name")}
        />
        <AdminTextField
          required
          type="email"
          label="Email"
          name="email"
          placeholder="name@spanidea.com"
          value={values.email}
          onChange={function (e) {
            setValue("email", sanitize.email(e.target.value));
          }}
          onBlur={formik.handleBlur}
          inputProps={{ maxLength: FIELD_MAX.email }}
          error={fieldError("email")}
          hint="Login details will be sent here"
        />
        <AdminTextField
          required
          label="Phone Number"
          name="phoneNumber"
          placeholder="Write Phone Number"
          value={values.phoneNumber}
          onChange={function (e) {
            setValue("phoneNumber", sanitize.phone(e.target.value));
          }}
          onBlur={formik.handleBlur}
          inputProps={{ inputMode: "numeric", maxLength: FIELD_MAX.phone }}
          InputProps={{ startAdornment: <PhonePrefix /> }}
          error={fieldError("phoneNumber")}
        />
        <AdminTextField
          required
          label="Designation"
          name="designation"
          placeholder="Write Designation"
          value={values.designation}
          onChange={function (e) {
            setValue("designation", sanitize.designation(e.target.value));
          }}
          onBlur={formik.handleBlur}
          inputProps={{ maxLength: FIELD_MAX.designation }}
          error={fieldError("designation")}
        />
        <AdminTextField
          required
          label="Work Location"
          name="workLocation"
          placeholder="Write Work Location"
          value={values.workLocation}
          onChange={function (e) {
            setValue("workLocation", sanitize.location(e.target.value));
          }}
          onBlur={formik.handleBlur}
          inputProps={{ maxLength: FIELD_MAX.location }}
          error={fieldError("workLocation")}
        />
        <AdminTextField
          select
          required
          label="HR Team Location"
          name="hrTeamLocation"
          placeholder="Select Location"
          value={values.hrTeamLocation || ""}
          onChange={function (e) {
            setValue("hrTeamLocation", e.target.value);
          }}
          onBlur={formik.handleBlur}
          error={fieldError("hrTeamLocation")}
        >
          {hasLegacyLocation ? (
            <MenuItem value={values.hrTeamLocation} disabled>
              {values.hrTeamLocation} (invalid location)
            </MenuItem>
          ) : null}
          {HR_TEAM_LOCATIONS.map(function (location) {
            return (
              <MenuItem key={location} value={location}>
                {location}
              </MenuItem>
            );
          })}
        </AdminTextField>
      </Box>
    </AdminDialog>
  );
}

function formatHrPhone(value) {
  var digits = String(value || "").replace(/\D/g, "").slice(-10);
  return digits.length === 10 ? "+91 " + digits.slice(0, 5) + " " + digits.slice(5) : digits;
}

function HrViewDrawer(props) {
  var hr = props.details || {};
  return (
    <AdminDrawer
      open={props.open}
      onClose={props.onClose}
      loading={props.loading}
      title="HR Record"
      subtitle="Contact details, team location and login access"
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <RecordProfileHeader
          photo={hr.profilePicture || hr.profilePhoto}
          name={hr.name}
          subtitle={hr.designation}
          email={hr.email}
        />
        <Box>
          <RecordSectionHeader title="Key Information" />
          <Box sx={RECORD_INFO_GRID_SX}>
            <RecordInfoItem label="Phone Number" value={formatHrPhone(hr.phoneNumber)} />
            <RecordInfoItem label="HR Team Location" value={hr.hrTeamLocation || hr.workLocation} />
            <RecordInfoItem label="Work Location" value={hr.workLocation} />
            <RecordInfoItem label="Login">
              <RecordLoginSwitch checked={props.isActive} onChange={props.onToggleActive} ariaLabel="Toggle HR login" />
            </RecordInfoItem>
          </Box>
        </Box>
      </Box>
    </AdminDrawer>
  );
}

/** Super Admin landing page: manage HR accounts. */
function AdminUsers() {
  var [rows, setRows] = useState([]);
  var [loading, setLoading] = useState(true);
  var [searchKey, setSearchKey] = useState("");

  var [dialogOpen, setDialogOpen] = useState(false);
  var [dialogMode, setDialogMode] = useState("add");
  var [dialogValues, setDialogValues] = useState(EMPTY_HR);
  var [editId, setEditId] = useState("");

  var [viewId, setViewId] = useState("");
  var [viewOpen, setViewOpen] = useState(false);
  var [viewLoading, setViewLoading] = useState(false);
  var [viewDetails, setViewDetails] = useState({});

  function loadList() {
    return SuperAdminAPI.getListAllHR()
      .then(function (res) {
        setRows(toRows(res.data));
      })
      .catch(function (err) {
        console.log(err);
      })
      .finally(function () {
        setLoading(false);
      });
  }

  useEffect(function () {
    loadList();
  }, []);

  var visibleRows = useMemo(
    function () {
      return rows.filter(function (row) {
        return matchesHrSearch(row, searchKey);
      });
    },
    [rows, searchKey]
  );

  var exportRows = useMemo(
    function () {
      return visibleRows.map(function (row, index) {
        return {
          "Sr.No.": index + 1,
          Name: row.name || "",
          Designation: row.designation || "",
          Email: row.email || "",
          "Phone Number": row.phoneNumber || "",
          "Work Location": row.workLocation || "",
          "HR Team Location": row.hrTeamLocation || "",
          Login: row.isActive ? "Active" : "Inactive",
        };
      });
    },
    [visibleRows]
  );

  function handleActiveInactive(nextActive, id) {
    SuperAdminAPI.activeInactiveHR(id, nextActive ? "active" : "in-active")
      .then(function (res) {
        if (res.success) {
          toast.success(res.message, TOAST_OPTS);
          setRows(function (prev) {
            return prev.map(function (row) {
              return row.id === id ? Object.assign({}, row, { isActive: nextActive }) : row;
            });
          });
          if (id === viewId) {
            setViewDetails(function (prev) {
              return Object.assign({}, prev, { isActive: nextActive });
            });
          }
        } else {
          toast.error(res.message, TOAST_OPTS);
        }
      })
      .catch(function (err) {
        console.log(err);
        toast.error("Unable to update status", TOAST_OPTS);
      });
  }

  function openAdd() {
    setEditId("");
    setDialogValues(EMPTY_HR);
    setDialogMode("add");
    setDialogOpen(true);
  }

  function openEdit(id) {
    SuperAdminAPI.getHR(id)
      .then(function (res) {
        if (!res.success) {
          toast.error(res.message || "Unable to load HR details", TOAST_OPTS);
          return;
        }
        var data = res.data || {};
        setEditId(id);
        setDialogValues({
          name: data.name || "",
          email: data.email || "",
          phoneNumber: String(data.phoneNumber || "").replace(/\D/g, "").slice(-10),
          designation: data.designation || "",
          workLocation: data.workLocation || "",
          hrTeamLocation: matchLocation(data.hrTeamLocation || data.workLocation),
        });
        setDialogMode("edit");
        setDialogOpen(true);
      })
      .catch(function (err) {
        console.log(err);
        toast.error("Unable to load HR details", TOAST_OPTS);
      });
  }

  function openView(id) {
    setViewId(id);
    setViewDetails({});
    setViewOpen(true);
    setViewLoading(true);
    SuperAdminAPI.getHR(id)
      .then(function (res) {
        setViewDetails(res.data || {});
      })
      .catch(function (err) {
        console.log(err);
        toast.error("Unable to load HR details", TOAST_OPTS);
      })
      .finally(function () {
        setViewLoading(false);
      });
  }

  function handleSubmit(values) {
    var payload = {
      name: cleanText(values.name),
      email: values.email.trim(),
      phoneNumber: values.phoneNumber,
      designation: cleanText(values.designation),
      workLocation: cleanText(values.workLocation),
      hrTeamLocation: values.hrTeamLocation,
    };
    var request = dialogMode === "edit" ? SuperAdminAPI.updateHR(editId, payload) : SuperAdminAPI.createHR(payload);
    return request
      .then(function (res) {
        if (res.success) {
          toast.success(res.message || (dialogMode === "edit" ? "HR updated" : "HR created"), TOAST_OPTS);
          setDialogOpen(false);
          loadList();
          if (dialogMode === "edit" && editId === viewId && viewOpen) openView(editId);
        } else {
          toast.error(res.message, TOAST_OPTS);
        }
      })
      .catch(function (err) {
        console.log(err);
        var message =
          (err && err.response && err.response.data && err.response.data.message) || "Unable to save HR";
        toast.error(message, TOAST_OPTS);
      });
  }

  var columns = [
    {
      field: "sNo",
      header: "Sr.No.",
      width: 76,
      render: function (row, index) {
        return index + 1;
      },
    },
    { field: "name", header: "Name", sortable: true, minWidth: 180, maxWidth: 260 },
    { field: "designation", header: "Designation", muted: true, minWidth: 160, maxWidth: 240 },
    { field: "email", header: "Email", muted: true, minWidth: 220, maxWidth: 300 },
    { field: "phoneNumber", header: "Phone Number", muted: true, width: 160 },
    { field: "workLocation", header: "Work Location", muted: true, width: 150 },
    { field: "hrTeamLocation", header: "HR Team Location", muted: true, width: 180 },
    {
      field: "isActive",
      header: "Login",
      width: 90,
      render: function (row) {
        return (
          <Switch
            checked={Boolean(row.isActive)}
            onChange={function (e, checked) {
              handleActiveInactive(checked, row.id);
            }}
            inputProps={{ "aria-label": "Toggle login for " + (row.name || "HR") }}
            sx={ADMIN_SWITCH_SX}
          />
        );
      },
    },
    {
      field: "actions",
      header: "Actions",
      width: 110,
      render: function (row) {
        return (
          <Box sx={{ display: "flex", gap: "4px" }}>
            <Tooltip title="Edit">
              <IconButton
                aria-label={"Edit " + (row.name || "HR")}
                onClick={function () {
                  openEdit(row.id);
                }}
                sx={ACTION_ICON_BUTTON_SX}
              >
                <AdminIcon name="PencilSimple" size={ADMIN_ICON_SIZE.action} />
              </IconButton>
            </Tooltip>
            <Tooltip title="View">
              <IconButton
                aria-label={"View " + (row.name || "HR")}
                onClick={function () {
                  openView(row.id);
                }}
                sx={ACTION_ICON_BUTTON_SX}
              >
                <AdminIcon name="Eye" size={ADMIN_ICON_SIZE.action} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <AdminPage>
      <AdminSheet viewport>
        <AdminSheetHeader
          title="Manage HR"
          action={
            <Button
              variant="contained"
              onClick={openAdd}
              startIcon={<AdminIcon name="Plus" size={ADMIN_ICON_SIZE.button} />}
              sx={[ADMIN_BUTTON_SX.contained, ADMIN_BUTTON_SX.withStartIcon]}
            >
              Add HR
            </Button>
          }
        />

        <Box sx={{ px: "20px", py: "16px", display: "flex", flexDirection: "column", gap: "16px", flex: 1, minHeight: 0 }}>
          <AdminListToolbar
            search={searchKey}
            onSearchChange={setSearchKey}
            onClearFilters={searchKey.trim() ? function () { setSearchKey(""); } : null}
            exportData={exportRows}
            exportFilename="hr-list.csv"
          />

          <AdminTable
            fill
            columns={columns}
            rows={visibleRows}
            loading={loading}
            resetKey={searchKey}
            emptyText={searchKey ? "No HR matches your search" : "No HR added yet"}
          />
        </Box>
      </AdminSheet>

      <HrDialog
        open={dialogOpen}
        mode={dialogMode}
        initialValues={dialogValues}
        onClose={function () {
          setDialogOpen(false);
        }}
        onSubmit={handleSubmit}
      />

      <HrViewDrawer
        open={viewOpen}
        loading={viewLoading}
        details={viewDetails}
        isActive={Boolean(viewDetails.isActive)}
        onClose={function () {
          setViewOpen(false);
        }}
        onToggleActive={function (checked) {
          handleActiveInactive(checked, viewId);
        }}
      />
    </AdminPage>
  );
}

export default AdminUsers;
